/**
 * The game log feed in uiState: one push that applies the log's settings,
 * stamps the id and trims to the cap, and the player lines the kill, join and
 * leave events share. The other builders stay with the events that raise
 * them, in Btr and UI; they describe the event and leave the filtering here.
 */
import { ref } from 'valtio/vanilla';
import { uiState, type GameLogEntry, type GameLogPlayer } from '../ui/UIState';
import { GAME_DEFAULTS } from './Config';
import type Player from './Player';
import { session } from './Session';
import { settings } from './Settings';

/** How many entries the feed keeps; the oldest drops off past this. */
const MAX_ENTRIES = 100;

/** The settings the feed reads: its own switch, one per kind of entry, and the bots filter. */
type GameLogSetting = 'gameLog' | 'gameLogKills' | 'gameLogJoinLeave' | 'gameLogCtf' | 'gameLogBtr' | 'ignoreBots';

/** The setting that shows each kind of entry, besides the log's own switch. */
const ENTRY_SETTING: Readonly<Record<GameLogEntry['type'], GameLogSetting>> = {
  kill: 'gameLogKills',
  join: 'gameLogJoinLeave',
  leave: 'gameLogJoinLeave',
  'ctf-flag': 'gameLogCtf',
  'btr-win': 'gameLogBtr',
  'players-alive': 'gameLogBtr',
};

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** An entry as its builder describes it; the push stamps the id. */
type GameLogEvent = DistributiveOmit<GameLogEntry, 'id'>;

let nextEntryId = 0;

function isSettingOn(setting: GameLogSetting): boolean {
  return settings.game?.[setting] ?? GAME_DEFAULTS[setting];
}

/**
 * Whether the feed takes this kind of entry right now. Read as each event
 * arrives, so a changed setting applies from then on, and a skipped entry
 * never reaches uiState.
 */
function shows(type: GameLogEntry['type']): boolean {
  return isSettingOn('gameLog') && isSettingOn(ENTRY_SETTING[type]);
}

/** "Hide bots" also keeps bot-only lines out: a bot killing a bot, and bots joining or leaving. */
function hidesBots(): boolean {
  return isSettingOn('ignoreBots');
}

/**
 * Appends the entry and drops the oldest once past the cap.
 *
 * The entry goes in as a Valtio ref: it is a plain record that is never
 * changed once pushed, so Valtio builds no proxy for it, the shift past the
 * cap moves plain objects instead of re-pointing a listener per entry, and a
 * write anywhere else in uiState no longer walks the feed's entries to
 * refresh its version. The array itself stays a proxy, so a push or a shift
 * still wakes the log.
 */
function append(event: GameLogEvent): void {
  uiState.gameLogEntries.push(ref({ ...event, id: nextEntryId++ }));
  if (uiState.gameLogEntries.length > MAX_ENTRIES) {
    uiState.gameLogEntries.shift();
  }
}

/** Appends an entry, if the settings show its kind. */
export function pushGameLogEntry(event: GameLogEvent): void {
  if (shows(event.type)) {
    append(event);
  }
}

/** The plain player summary a log line carries. */
function toGameLogPlayer(player: Player): GameLogPlayer {
  return {
    id: player.id,
    name: player.name,
    flag: player.flag,
    team: player.team,
    isMe: player.id === session.myID,
    isBot: player.bot,
  };
}

/** A kill line. A kill with a human on either side stays when bots are hidden. */
export function pushGameLogKill(killer: Player, victim: Player): void {
  if (shows('kill') && !(hidesBots() && killer.bot && victim.bot)) {
    append({ type: 'kill', killer: toGameLogPlayer(killer), victim: toGameLogPlayer(victim) });
  }
}

/** A join line, unless the player is a hidden bot. */
export function pushGameLogJoin(player: Player): void {
  if (shows('join') && !(hidesBots() && player.bot)) {
    append({ type: 'join', player: toGameLogPlayer(player) });
  }
}

/** A leave line, unless the player is a hidden bot. */
export function pushGameLogLeave(player: Player): void {
  if (shows('leave') && !(hidesBots() && player.bot)) {
    append({ type: 'leave', player: toGameLogPlayer(player) });
  }
}
